<template>
    <div :data-testid="props.dataTestid">
        <div
            :class="{'freelancer-skills-autocomplete__global-skills--disabled': props.disabled}"
            class="freelancer-skills-autocomplete__global-skills"
        >
            <AutoComplete
                v-model="globalSkillsInput"
                :suggestions="searchingSkills"
                :placeholder="props.placeholder ?? t('profile.section.expertises.edit.freelancer.placeholder')"
                :empty-search-message="t('generic.no.results.found')"
                scroll-height="40vh"
                option-label="label"
                :option-disabled="isSkillOptionDisabled"
                :auto-option-focus="true"
                :disabled="props.disabled"
                :data-testid="`${props.dataTestid}-autocomplete`"
                :invalid="invalid"
                data-joy-form-field
                @complete="searchGlobalSkills"
                @option-select="handleUpdateGlobalSkills"
                @keydown.escape.stop
            >
                <template #option="slotProps">
                    <div
                        class="freelancer-skills-autocomplete__global-skills__option"
                        :class="{'freelancer-skills-autocomplete__global-skills__option--selected': slotProps.option.selected}"
                    >
                        <template v-if="slotProps.option.selected">
                            <div class="freelancer-skills-autocomplete__global-skills__option__content">
                                <div>{{ slotProps.option.label }}</div>
                                <div
                                    v-if="props.showOccurrences && slotProps.option.occurrences > 0"
                                    class="freelancer-skills-autocomplete__global-skills__option__counter"
                                >
                                    {{ t('profile.section.skill.set.edition.globalskills.occurences', {nbOccurences: slotProps.option.occurrences}) }}
                                </div>
                            </div>
                            <VJoyIcon name="check" color="neutral" />
                        </template>
                        <template v-else-if="isTagToCreate(slotProps.option)">
                            <!-- eslint-disable vue/no-v-html -->
                            <span v-html="t('profile.section.skill.set.edition.globalskills.create.label', [escapeHtml(slotProps.option.label)])" />
                            <!-- eslint-enable vue/no-v-html -->
                            <span v-if="props.showOccurrences" class="freelancer-skills-autocomplete__global-skills__option__counter">
                                &nbsp;({{ t('profile.section.skill.set.edition.globalskills.occurences', {nbOccurences: 0}) }})
                            </span>
                        </template>
                        <template v-else-if="props.showOccurrences && slotProps.option.occurrences > 0">
                            <div>{{ slotProps.option.label }}</div>
                            <div class="freelancer-skills-autocomplete__global-skills__option__counter">
                                {{ t('profile.section.skill.set.edition.globalskills.occurences', {nbOccurences: slotProps.option.occurrences}) }}
                            </div>
                        </template>
                        <div v-else>{{ slotProps.option.label }}</div>
                    </div>
                </template>
            </AutoComplete>
            <VJoyWrapper
                v-if="disabled && disabledLabel"
                class="freelancer-skills-autocomplete__global-skills__disabled_label"
                column-gap="1"
                :data-testid="`${props.dataTestid}-autocomplete-disabled-label`"
            >
                <VJoyIcon name="info-circle" color="neutral" />
                <span>{{ disabledLabel }}</span>
            </VJoyWrapper>
            <VJoyFormError
                v-if="errors.length"
                class="freelancer-skills-autocomplete__errors"
                :errors="errors"
                :data-testid="`${props.dataTestid}-error`"
            />
        </div>

        <VJoyLabel
            v-if="props.labelForSelection && selectedGlobalSkills.length > 0"
            :data-testid="`${props.dataTestid}--label-for-selection`"
            :for="`${props.dataTestid}-list`"
        >
            {{ t(props.labelForSelection) }}
        </VJoyLabel>
        <VJoyWrapper
            v-if="props.showTagList"
            :data-testid="`${props.dataTestid}-list`"
            :name="`${props.dataTestid}-list`"
            class="freelancer-skills-autocomplete__global-skills__list"
            column-gap="2"
            row-gap="2"
            no-margin
        >
            <VJoyTag
                v-for="(selectedSkill, index) in selectedGlobalSkills"
                :id="`skill-autocomplete--${index}`"
                :key="`skill-autocomplete--${index}`"
                :data-testid="`${props.dataTestid}-tag-${index}`"
                class="freelancer-skills-autocomplete__tag"
                removable
                @remove="handleGlobalSkillRemove(selectedSkill)"
            >
                {{ selectedSkill.label }}
            </VJoyTag>
            <VJoyTag
                v-for="(selectedSkill, index) in emptyTags"
                :id="`skill-autocomplete-empty-tag--${index}`"
                :key="`skill-autocomplete-empty-tag--${index}`"
                :data-testid="`${props.dataTestid}-empty-tag-${index}`"
                class="freelancer-skills-autocomplete__empty-tag"
                variant="secondary"
            >
                {{ selectedSkill.label }}
            </VJoyTag>
        </VJoyWrapper>
    </div>
</template>
<script setup lang="ts">
    import {computed, ref, toRef} from 'vue';
    import AutoComplete from 'primevue/autocomplete';
    import {useTranslation} from '#imports';
    import {escapeHtml} from '@malt/sanitize';
    import {pushVJoySnackbar, VJoyIcon, VJoyLabel, VJoyTag, VJoyWrapper, VJoyFormError} from '@maltjoy/core-vue';
    import {useSkillSearch} from '~/features/skills/composables/useSkillSearch';
    import type {Skill as SearchingSkill} from '~/features/skills/types/Skill';
    import {type Skill as SkillApi, SkillOriginEnum, SkillTypeEnum} from '~/openapi';
    import type {TranslationKey} from '#build/i18n';

    export type SkillLabel = {
        label: string;
    };
    type SkillAutocompleteProps = {
        modelValue: SkillLabel[] | SkillApi[];
        rulesGlobalSkillLabelMinLength: number;
        rulesGlobalSkillLabelMaxLength: number;
        dataTestid?: string;
        disabled?: boolean;
        disabledLabel?: string;
        labelForSelection?: TranslationKey;
        placeholder?: string;
        showEmptyTags?: boolean;
        showOccurrences?: boolean;
        showTagList?: boolean;
        invalid?: boolean;
        errors?: string[];
    };

    type SkillAutocompleteQuery = {
        query: string;
    };

    type SkillAutocompleteResult = {
        value: {label: string};
    };

    const props = withDefaults(defineProps<SkillAutocompleteProps>(), {
        rulesGlobalSkillLabelMaxLength: 150,
        rulesGlobalSkillLabelMinLength: 1,
        dataTestid: 'freelancer-skills-autocomplete',
        disabled: false,
        showEmptyTags: false,
        showOccurrences: false,
        showTagList: false,
        invalid: false,
        errors: () => [],
    });
    const emit = defineEmits<{
        'skill-add': [value: SkillLabel | SkillApi];
        'skill-remove': [value: SkillLabel | SkillApi];
        'update:modelValue': [value: SkillLabel[] | SkillApi[]];
    }>();

    // Composables
    const {t} = useTranslation();
    const {autocomplete} = useSkillSearch();

    // Refs
    const searchingSkills = ref<SearchingSkill[]>([]);
    const globalSkillsInput = ref('');
    const selectedGlobalSkills = toRef(props, 'modelValue');
    const errors = toRef(props, 'errors');

    // Computed
    const emptyTags = computed((): SkillLabel[] => {
        if (!props.showEmptyTags) {
            return [];
        }
        const limit = 5;
        const selectedSkillsCount = selectedGlobalSkills.value?.length || 0;

        if (selectedSkillsCount >= limit) {
            return [];
        }

        return Array(limit - selectedSkillsCount).fill({
            label: '',
        });
    });

    // Handlers
    function handleUpdateGlobalSkills(event: SkillAutocompleteResult) {
        const skillToAdd = event.value.label;

        if (skillToAdd.length < props.rulesGlobalSkillLabelMinLength) {
            return;
        }

        if (skillToAdd.length > props.rulesGlobalSkillLabelMaxLength) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.creation.completion.wizard.details.skills.error.label.too.long', {0: props.rulesGlobalSkillLabelMaxLength}),
                    level: 'error',
                },
            });

            return;
        }

        if (props.modelValue.some((globalSkill) => globalSkill.label === skillToAdd)) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.creation.completion.wizard.details.skills.error.duplicate'),
                    level: 'error',
                },
            });

            return;
        }

        const newSelectedSkill: SkillApi = {
            id: skillToAdd,
            label: skillToAdd,
            type: SkillTypeEnum.Global,
            origin: SkillOriginEnum.Manual,
        };

        const newValue = props.modelValue;
        selectedGlobalSkills.value.push(newSelectedSkill);

        emit('skill-add', newSelectedSkill);
        emit('update:modelValue', newValue);

        clearInput();
    }

    function handleGlobalSkillRemove(skillToRemove: SkillLabel) {
        const newValue = props.modelValue.filter((skill) => skill.label !== skillToRemove.label);

        emit('skill-remove', skillToRemove);
        emit('update:modelValue', newValue);
    }

    // Functions
    async function searchGlobalSkills(event: SkillAutocompleteQuery) {
        const selectedLabels = props.modelValue.map((skill) => skill.label);
        searchingSkills.value = await autocomplete(event.query, selectedLabels);
    }

    function isSkillOptionDisabled(skill: SearchingSkill): boolean {
        return !!skill.selected;
    }

    function isTagToCreate({label, occurrences, selected}: SearchingSkill): boolean {
        return label === globalSkillsInput.value && !occurrences && !selected;
    }

    function clearInput() {
        globalSkillsInput.value = '';
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @import 'primeicons/primeicons.css';

    .freelancer-skills-autocomplete {
        &__global-skills {
            margin-bottom: var(--joy-core-spacing-4);

            &__disabled_label {
                margin-top: var(--joy-core-spacing-2);
            }

            &__label {
                font-weight: var(--joy-font-weight-normal);
                margin-bottom: var(--joy-core-spacing-2);
            }
            &__list {
                display: flex;
                gap: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
                flex-wrap: wrap;
            }

            &__option {
                width: 100%;

                &--selected {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: var(--joy-color-neutral-40);
                }

                &__counter {
                    @include text.getFontBySize(primary-200);
                    color: var(--joy-color-neutral-40);
                }

                &__tag-to-create {
                    font-weight: var(--joy-font-weight-bold);
                    color: var(--joy-color-neutral-70);
                }
            }

            &--disabled {
                cursor: not-allowed;
            }
        }

        &__errors {
            margin-top: var(--joy-core-spacing-2) !important;
            margin-bottom: 0;
        }

        &__empty-tag {
            min-width: 80px;
            max-width: 50%;
            flex-grow: 1;
            &:nth-child(odd) {
                min-width: 100px;
            }
        }
    }
</style>
